import { ReactNode } from 'react';

import {
  Card,
  Box as Flex,
  Link,
  Typography as Text,
  makeStyles,
} from '@material-ui/core';

import classNames from 'classnames';

import {
  PageLayoutMui as PageLayout,
  useAuth,
  useSupportMessenger,
} from '@spotify/confidence-core-react';

import { PageBreadcrumbs } from '../../../../PageBreadcrumbs';
import { useOptionalPageBridgeProps } from '../../../../utils';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '60vh',
  },
  description: {
    textAlign: 'center',
    maxWidth: '520px',
  },
  cards: {
    maxWidth: '720px',
  },
  card: {
    flex: 1,
    padding: 'var(--encore-spacing-base)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--encore-spacing-tighter-2)',
  },
  link: {
    display: 'inline',
    textDecoration: 'underline',
  },
}));

export type ResourceErrorPageProps = {
  title: string;
  error: ReactNode;
};

// eslint-disable-next-line @spotify-confidence/page-requires-tracking -- rendered inside already-tracked pages via ResourceGuard; tracking happens at the parent
export const ResourceErrorPage = ({ title, error }: ResourceErrorPageProps) => {
  const bridgeProps = useOptionalPageBridgeProps();
  const classes = useStyles();
  const { user } = useAuth();
  const { openChat } = useSupportMessenger();

  const handleContactSupport = () => {
    openChat(
      user?.name && user?.email
        ? { name: user.name, email: user.email }
        : undefined,
    );
  };

  return (
    <PageLayout
      title={null}
      headerBreadcrumbs={<PageBreadcrumbs />}
      narrow={bridgeProps?.narrow}
    >
      <div className={classNames(classes.container)}>
        <Flex
          display="flex"
          justifyContent="center"
          flexDirection="column"
          style={{
            gap: 'var(--encore-spacing-looser)',
          }}
        >
          <Flex display="flex" flexDirection="column" alignItems="center">
            <Text variant="subtitle2" color="textSecondary">
              Error
            </Text>
            <Text variant="h5" component="h2">
              {title}
            </Text>
          </Flex>
          <Flex
            display="flex"
            flexDirection="column"
            mb={4}
            alignItems="center"
          >
            <Text variant="body1" component="p" className={classes.description}>
              {error}
            </Text>
          </Flex>
          <Flex
            display="flex"
            flexDirection="row"
            className={classes.cards}
            style={{
              gap: 'var(--encore-spacing-looser)',
            }}
          >
            <Card className={classes.card}>
              <Text variant="subtitle2" style={{ fontWeight: 'bold' }}>
                Refresh the page
              </Text>
              <Text variant="body1" color="textSecondary" component="p">
                <Link
                  className={classes.link}
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    window.location.reload();
                  }}
                >
                  Try refreshing
                </Link>{' '}
                — the issue may be temporary
              </Text>
            </Card>
            <Card className={classes.card}>
              <Text variant="subtitle2" style={{ fontWeight: 'bold' }}>
                Start fresh
              </Text>
              <Text variant="body1" color="textSecondary" component="p">
                <Link href="/" className={classes.link}>
                  Go back to the homepage
                </Link>{' '}
                and find what you need
              </Text>
            </Card>
            <Card className={classes.card}>
              <Text variant="subtitle2" style={{ fontWeight: 'bold' }}>
                Need help?
              </Text>
              <Text variant="body1" color="textSecondary" component="p">
                If the problem persists, please{' '}
                <Link
                  className={classes.link}
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    handleContactSupport();
                  }}
                >
                  contact our support team
                </Link>
              </Text>
            </Card>
          </Flex>
        </Flex>
      </div>
    </PageLayout>
  );
};
